import { useEffect, useState } from 'react'
import ApiInviteCodes from '../api/ApiInviteCodes'
import ApiUsers from '../api/ApiUsers'
import useAuth from './useAuth'

/**
 * Custom hook for managing invite codes data and operations
 * Extracts all data logic from AdminInviteCodes component following SRP
 */
export default function useInviteCodes() {
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [creators, setCreators] = useState({}) // id -> display label
  const { user } = useAuth()

  const loadCodes = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const data = await ApiInviteCodes.getAll()
      setCodes(data)
      
      // Build creators map for display
      const creatorIds = Array.from(
        new Set(
          (data || [])
            .map((c) => c.created_by_user_id)
            .filter((id) => typeof id === 'number' && id > 0)
        )
      )
      
      if (creatorIds.length) {
        try {
          const allUsers = await ApiUsers.getAll()
          const needed = new Set(creatorIds)
          const map = {}
          for (const u of allUsers || []) {
            if (needed.has(u.id)) {
              const name = [u.firstname, u.lastname]
                .filter(Boolean)
                .join(' ')
                .trim()
              map[u.id] = name || u.email || `User #${u.id}`
            }
          }
          setCreators(map)
        } catch (e) {
          // Non-fatal: if user fetch fails, we still render IDs
           
          console.warn('Failed to load creator user info', e)
        }
      } else {
        setCreators({})
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createCode = async (data) => {
    try {
      setError(null)
      const resp = await ApiInviteCodes.create(data)
      
      // Update local state with new code
      const newCode = {
        id: resp.id,
        code: resp.code,
        expires_at: data.expires_at || null,
        disabled: false,
        max_uses: data.max_uses,
        created_by_user_id: user?.id || null,
        used_by: [],
      }
      
      setCodes(prevCodes => [...prevCodes, newCode])
      
      // Ensure creators map includes current user for immediate display
      if (user?.id && !creators[user.id]) {
        const name = [user.firstname, user.lastname]
          .filter(Boolean)
          .join(' ')
          .trim()
        setCreators((prev) => ({
          ...prev,
          [user.id]: name || user.email || `User #${user.id}`,
        }))
      }
      
      return resp
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const disableCode = async (id) => {
    try {
      setError(null)
      await ApiInviteCodes.disable(id)
      setCodes(codes.map((c) => (c.id === id ? { ...c, disabled: true } : c)))
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const deleteCode = async (id) => {
    try {
      setError(null)
      await ApiInviteCodes.remove(id)
      setCodes(codes.filter((c) => c.id !== id))
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  useEffect(() => {
    loadCodes()
  }, [])

  return {
    // State
    codes,
    loading,
    error,
    creators,
    
    // Actions
    loadCodes,
    createCode,
    disableCode,
    deleteCode,
  }
}